import { HttpDownloadFileResult } from "@capacitor-community/http";
import {
  capSQLiteChanges,
  capSQLiteValues,
  SQLiteDBConnection,
} from "@capacitor-community/sqlite";
import moment from "moment";
import agent from "../api/agent";
import { sqlite } from "../PlayerApp";
//import { sqlite } from "../Guest";
import filesystem from "../filesystem/filesystem";
import { ICompany } from "../models/company";
import { IPlayerContent } from "../models/content";
import { ITable } from "../models/db";
import { IBoardTreePageDto } from "../models/dto";
import { IUserMeeting } from "../models/meeting";
import { INode } from "../models/node";
import { INativeAppConf } from "../models/offline";
import { ITenant } from "../models/tenant";
import { IUser } from "../models/user";
import queries from "./queries";
import tables from "./tables";

const dbName = "DbTT";
const initialTables: ITable[] = [
  { name: tables.Companies, createQuery: queries.Companies.create },
  { name: tables.Tenants, createQuery: queries.Tenants.create },
  { name: tables.NativeAppConf, createQuery: queries.NativeAppConf.create },
  { name: tables.Users, createQuery: queries.Users.create },
  { name: tables.Meetings, createQuery: queries.Meetings.create },
  { name: tables.Nodes, createQuery: queries.Nodes.create },
  { name: tables.Boards, createQuery: queries.Boards.create },
  { name: tables.BoardNodes, createQuery: queries.BoardNodes.create },
];

const openDbConnection = async () => {
  const connectionExists = await sqlite.isConnection(dbName);
  const connectionConsistency = await sqlite.checkConnectionsConsistency();
  let db = null;
  if (
    !connectionExists ||
    !connectionExists.result ||
    !connectionConsistency ||
    !connectionConsistency.result
  ) {
    db = await sqlite.createConnection(dbName, false, "no-encryption", 1);
    await db.open();
  } else {
    db = await sqlite.retrieveConnection(dbName);
  }
  return db;
};

const closeDbConnection = async () => {
  const connectionExists = await sqlite.isConnection(dbName);
  if (connectionExists && connectionExists.result)
    await sqlite.closeConnection(dbName);
};

export const initializeDb = async () => {
  let success = false;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      await Promise.all(
        initialTables.map(async (t) => {
          let sqliteResult = await db.isTable(t.name);
          let tableExists = sqliteResult.result?.valueOf() ?? false;
          if (!tableExists)
            await db.execute(t.createQuery);
        })
      );
      success = true;
    } catch (error) {
      //toast.error("exception:" + JSON.stringify(error));
      success = false;
    } finally {
      await closeDbConnection();
    }
  }
  return success;
};

export const updateCompany = async (company: ICompany) => {
  let success = true;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();
      await db.execute(queries.Delete.all(tables.Companies));

      let sqliteChanges = await db.run(queries.Companies.insert, [
        company.id,
        company.name,
        company.displayName,
      ]);

      success = success && sqliteChanges.changes?.changes === 1;
      await filesystem.Download.companyLogo(company, "logo.png");
      await filesystem.Download.companyLogo(company, "background.png");  
    } catch (error) {
      
      success = false;
    } finally {
      await closeDbConnection();
    }
  }

  return success;
};

export const updateTenantsList = async (tenants: ITenant[]) => {
  let success = true;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      await db.execute(queries.Delete.all(tables.Tenants));
      await Promise.all(
        tenants.map(async (t) => {
          let sqliteChanges = await db.run(queries.Tenants.insert, [
            t.id,
            t.name,
            t.path,
            t.theme,
            t.featured,
            t.order
          ]);
          success = success && sqliteChanges.changes?.changes === 1;
          await filesystem.Download.tenantLogo(t, "logo.png");
          await filesystem.Download.tenantLogo(t, "logo_sidebar.png");
          await filesystem.Download.tenantCss(t, "theme.css");
        })
      );
    } catch (error) {
      success = false;
    } finally {
      await closeDbConnection();
    }
  }
  return success;
};

export const getCompanySqlite = async () => {
  let company: ICompany | null = null;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      let sqliteValues: capSQLiteValues;
      sqliteValues = await db.query(queries.Select.first(tables.Companies));
      if (sqliteValues && sqliteValues.values) {
        const firstRecord = sqliteValues.values[0];
        company = {
          id: firstRecord.Id,
          name: firstRecord.Name,
          displayName: firstRecord.DisplayName,
          domain: "",
          defaultLoginMethod: "",
          alternativeLoginMethod: "",
          allowRegistration: false,
          allowBroadcast: false,
          allowGuest: false,
          displayTenantNameInTenantList:true,
          hiddenDuration:false,
          hiddenEndHour:false,
          hiddenEndDate:false,
          hiddenStartDate:false,
          hiddenStartHour:false,
          hiddenNumDoc:false,
          sidebarButtonBorder: "none",
          isGlobalGuestFullyEnabled: false,
          isLoginWithUsernameEnabled: false,
          disabledCustom2FactorForSubnet: false,
          languageDefault: ""

        };
      }
    } catch (error) {
      company = null;
    } finally {
      await closeDbConnection();
    }
  }
  return company;
};

export const getTenantList = async () => {
  let tenants: ITenant[] | null = null;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      let sqliteValues: capSQLiteValues;
      sqliteValues = await db.query(queries.Tenants.retrieveOrderedList);
      if (sqliteValues && sqliteValues.values) {
        tenants = [];
        sqliteValues.values.forEach((t) =>
          tenants!.push({
            id: t.Id,
            name: t.Name,
            path: t.Path,
            theme: t.Theme,
            featured: t.Featured,
            order: t.OrderPos,
            redirectDOC: false,
            inactivityThresholds: t.InactivityThresholds
          })
        );
      }
    } catch (error) {
      tenants = [];
    } finally {
      await closeDbConnection();
    }
  }
  return tenants;
};

export const getNativeAppConf = async () => {
  let conf: INativeAppConf | null = null;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      let sqliteValues: capSQLiteValues;
      sqliteValues = await db.query(queries.Select.first(tables.NativeAppConf));
      if (sqliteValues && sqliteValues.values && sqliteValues.values[0]) {
        let record = sqliteValues.values[0];
        conf = {
          id: record.Id,
          requirePin: record.RequirePin,
          pin: record.Pin,
        };
      }
    } catch (error) {
      conf = null;
    } finally {
      await closeDbConnection();
    }
  }
  return conf;
};

export const setNativeAppConf = async (pin?: number) => {
  let conf: INativeAppConf | null = null;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();
      const requirePinCol = pin && pin > 0 ? true : false;
      const pinCol = pin && pin > 0 ? pin : undefined;

      conf = {
        id: 0,
        requirePin: requirePinCol,
        pin: pinCol,
      };

      let sqliteValues: capSQLiteValues;
      let sqliteChanges: capSQLiteChanges;
      sqliteValues = await db.query(queries.Select.first(tables.NativeAppConf));
      if (sqliteValues && sqliteValues.values && sqliteValues.values[0]) {
        let record = sqliteValues.values[0];
        conf.id = record.Id;
        sqliteChanges = await db.run(queries.NativeAppConf.update, [
          requirePinCol,
          pinCol,
          record.Id,
        ]);
      } else {
        sqliteChanges = await db.run(queries.NativeAppConf.insert, [
          requirePinCol,
          pinCol,
        ]);
      }

      if (sqliteChanges.changes?.changes !== 1) {
        //toast.error("Errore salvando le impostazioni");
        conf = null;
      }
    } catch (error) {
      conf = null;
    } finally {
      await closeDbConnection();
    }
  }
  return conf;
};

export const getAppUser = async () => {
  let user: IUser | null = null;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      let sqliteValues: capSQLiteValues;
      sqliteValues = await db.query(queries.Select.first(tables.Users));
      if (sqliteValues && sqliteValues.values && sqliteValues.values[0]) {
        let record = sqliteValues.values[0];
        user = {
          id: record.Id,
          userName: record.UserName,
          firstName: record.FirstName,
          lastName: record.LastName,
          email: "",
          logoutUrl:"",
          languageId: 0,
        };
      }
    } catch (error) {
      user = null;
    } finally {
      await closeDbConnection();
    }
  }
  return user;
};

export const updateAppUser = async (user: IUser) => {
  let success = false;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      let dbUser: capSQLiteValues;
      let sqliteChanges: capSQLiteChanges;
      dbUser = await db.query(queries.Select.first(tables.Users));
      if (dbUser && dbUser.values && dbUser.values[0]) {
        let record = dbUser.values[0];
        if (
          record.Id !== user.id ||
          record.UserName !== user.userName ||
          record.FirstName !== user.firstName ||
          record.LastName !== user.lastName
        ) {
          sqliteChanges = await db.run(queries.Users.update, [
            user.userName,
            user.firstName,
            user.lastName,
            record.Id,
          ]);
          success = sqliteChanges.changes?.changes === 1;
        } else success = true;
      } else {
        sqliteChanges = await db.run(queries.Users.insert, [
          user.id,
          user.userName,
          user.firstName,
          user.lastName,
        ]);
        success = sqliteChanges.changes?.changes === 1;
      }
    } catch (error) {
      //toast.error(JSON.stringify(error));
      success = false;
    } finally {
      await closeDbConnection();
    }
  }
  return success;
};

export const updateMeeting = async (meeting: IUserMeeting, tenant: ITenant) => {
  let success = false;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      let dbMeeting: capSQLiteValues;
      let sqliteChanges: capSQLiteChanges;
      dbMeeting = await db.query(
        queries.Select.id(tables.Meetings, meeting.id)
      );
      if (dbMeeting && dbMeeting.values && dbMeeting.values[0]) {
        const { Id, Title, StartDate, EndDate, LastUpdateDate } =
          dbMeeting.values[0];
        if (
          Id !== meeting.id ||
          Title !== meeting.title ||
          !moment(StartDate).isSame(meeting.startDate) ||
          !moment(EndDate).isSame(meeting.endDate) ||
          !moment(LastUpdateDate).isSame(meeting.lastUpdateDate)
        ) {
          sqliteChanges = await db.run(queries.Meetings.update, [
            meeting.title,
            meeting.startDate,
            meeting.endDate,
            meeting.lastUpdateDate,
            tenant.id,
            Id,
          ]);
          success = sqliteChanges.changes?.changes === 1;
        } else success = true;
      } else {
        sqliteChanges = await db.run(queries.Meetings.insert, [
          meeting.id,
          meeting.title,
          meeting.startDate,
          meeting.endDate,
          meeting.lastUpdateDate,
          tenant.id,
        ]);
        success = sqliteChanges.changes?.changes === 1;
      }
    } catch (error) {
      //toast.error(JSON.stringify(error));
      success = false;
    } finally {
      await closeDbConnection();
    }
  }
  return success;
};

export const getMeetings = async (tenantId: number) => {
  let meetings: IUserMeeting[] = [];
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      let sqliteValues: capSQLiteValues;
      sqliteValues = await db.query(queries.Meetings.retriveByTenant(tenantId));
      if (sqliteValues && sqliteValues.values) {
        await Promise.all(
          sqliteValues.values.map(async (m) => {
            let meetingToAdd: IUserMeeting = {
              id: m.Id,
              title: m.Title,
              startDate: moment(m.StartDate).toDate(),
              endDate: moment(m.EndDate).toDate(),
              documentsCount: await countMeetingNodes(db, m.Id),
              lastUpdateDate: moment(m.LastUpdateDate).toDate(),
              hasEnabledPodium: false,
              hasEnabledCabolo: false,
  
            };
            meetings.push(meetingToAdd);
          })
        );
      }
    } catch (error) {
      meetings = [];
    } finally {
      await closeDbConnection();
    }
  }
  return meetings;
};

export const updateMeetingNodes = async (
  meeting: IUserMeeting,
  nodes: INode[]
) => {
  let success = true;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      await db.execute(queries.Nodes.deleteByMeeting(meeting.id));
      await Promise.all(
        nodes.map(async (n, index) => {
          success =
            success &&
            (await insertNodeRecursive(
              db,
              n,
              index,
              meeting.id,
              null,
              "Meetings"
            ));
        })
      );
    } catch (error) {
      //toast.error(JSON.stringify(error));
      success = false;
    } finally {
      await closeDbConnection();
    }
  }
  return success;
};

export const getMeetingNodes = async (meetingId: number) => {
  let nodes: INode[] = [];
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();
      nodes = await getNodeChildrenRecursive(db, meetingId, null, "Meetings");
    } catch (error) {
      nodes = [];
    } finally {
      await closeDbConnection();
    }
  }
  return nodes;
};

const countMeetingNodes = async (db: SQLiteDBConnection, meetingId: number) => {
  let totalCount: number = 0;
  try {
    let sqliteValues: capSQLiteValues;
    sqliteValues = await db.query(queries.Nodes.countByMeeting(meetingId));
    if (sqliteValues && sqliteValues.values && sqliteValues.values[0]) {
      totalCount = sqliteValues.values[0].TotalCount;
    }
  } catch (error) {
    //toast.error(JSON.stringify(error));
    totalCount = 0;
  }
  return totalCount;
};

export const getNodeContentId = async (
  nodeId: number,
  table: "Meetings" | "Boards"
) => {
  let contentId: number = 0;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      let sqliteValues: capSQLiteValues;
      if (table === "Meetings")
        sqliteValues = await db.query(queries.Select.id(tables.Nodes, nodeId));
      else
        sqliteValues = await db.query(
          queries.Select.id(tables.BoardNodes, nodeId)
        );
      if (sqliteValues && sqliteValues.values && sqliteValues.values[0]) {
        let record = sqliteValues.values[0];
        contentId = record.ContentId ?? 0;
      }
    } catch (error) {
      contentId = 0;
    } finally {
      await closeDbConnection();
    }
  }
  return contentId;
};

export const updateBoard = async (
  board: IBoardTreePageDto,
  tenant: ITenant
) => {
  let success = false;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      let dbBoard: capSQLiteValues;
      let sqliteChanges: capSQLiteChanges;
      dbBoard = await db.query(queries.Select.id(tables.Boards, board.boardId));
      if (dbBoard && dbBoard.values && dbBoard.values[0]) {
        const { Id, Name, LastUpdateDate } = dbBoard.values[0];
        if (
          Id !== board.boardId ||
          Name !== board.name ||
          !moment(LastUpdateDate).isSame(board.lastUpdateDate)
        ) {
          sqliteChanges = await db.run(queries.Boards.update, [
            board.name,
            board.lastUpdateDate,
            tenant.id,
            Id,
          ]);
          success = sqliteChanges.changes?.changes === 1;
        } else success = true;
      } else {
        sqliteChanges = await db.run(queries.Boards.insert, [
          board.boardId,
          board.name,
          board.lastUpdateDate,
          tenant.id,
        ]);
        success = sqliteChanges.changes?.changes === 1;
      }
    } catch (error) {
      //toast.error(JSON.stringify(error));
      success = false;
    } finally {
      await closeDbConnection();
    }
  }
  return success;
};

export const updateBoardNodes = async (board: IBoardTreePageDto) => {
  let success = true;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();

      await db.execute(queries.BoardNodes.deleteByBoard(board.boardId));
      await Promise.all(
        board.tree.map(async (n, index) => {
          success =
            success &&
            (await insertNodeRecursive(
              db,
              n,
              index,
              board.boardId,
              null,
              "Boards"
            ));
        })
      );
    } catch (error) {
      //toast.error(JSON.stringify(error));
      success = false;
    } finally {
      await closeDbConnection();
    }
  }
  return success;
};

export const getMainBoard = async (tenantId: number) => {
  let board: IBoardTreePageDto | null = null;
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();
      let dbBoard: capSQLiteValues;
      dbBoard = await db.query(queries.Boards.retriveByTenant(tenantId));
      if (dbBoard && dbBoard.values && dbBoard.values[0]) {
        const { Id, Name, LastUpdateDate } = dbBoard.values[0];
        board = {
          boardId: Id,
          name: Name,
          tree: [],
          canEditNodes: false,
          lastUpdateDate: LastUpdateDate,
        };
      } else board = null;
    } catch (error) {
      board = null;
    } finally {
      await closeDbConnection();
    }
  }
  return board;
};

export const getBoardNodes = async (boardId: number) => {
  let nodes: INode[] = [];
  if (sqlite.isAvailable) {
    try {
      const db = await openDbConnection();
      nodes = await getNodeChildrenRecursive(db, boardId, null, "Boards");
    } catch (error) {
      nodes = [];
    } finally {
      await closeDbConnection();
    }
  }
  return nodes;
};

// PRIVATE METHODS
const insertNodeRecursive = async (
  db: SQLiteDBConnection,
  node: INode,
  order: number,
  externalId: number,
  parentId: number | null,
  table: "Meetings" | "Boards"
) => {
  let success = true;
  try {
    const nodeId = parseInt(node.key);

    // download contenuto
    let contentId: number | null = null;
    if (node.hasContent) {
      let content: IPlayerContent;
      let downloadResponse: HttpDownloadFileResult;
      if (table === "Meetings") {
        content = await agent.Meeting.content(nodeId);
        contentId = content && content.contentData ? content.contentData.id : 0;
        downloadResponse = await filesystem.Download.meetingPdf(
          externalId,
          content.contentData.id
        );
      } else {
        content = await agent.Board.content(nodeId);
        contentId = content && content.contentData ? content.contentData.id : 0;
        downloadResponse = await filesystem.Download.boardPdf(
          externalId,
          content.contentData.id
        );
      }
      if (!downloadResponse || !downloadResponse.path || contentId < 1) {
        //toast.error(`File for contentId ${contentId} not downloaded`);
        throw new Error(`File for contentId ${contentId} not downloaded`);
      }
    }

    // inserimento su DB
    const correctQuery =
      table === "Meetings" ? queries.Nodes.insert : queries.BoardNodes.insert;
    let sqliteChanges = await db.run(correctQuery, [
      nodeId,
      node.title,
      order,
      contentId,
      parentId,
      externalId,
    ]);
    success = success && sqliteChanges.changes?.changes === 1;

    //toast.warning(`${table}:${externalId} - NODE:${nodeId} - CONTENTID:${contentId} - RESULT:${success}`);

    await Promise.all(
      node.children.map(async (n, index) => {
        success =
          success &&
          (await insertNodeRecursive(db, n, index, externalId, nodeId, table));
      })
    );
  } catch (error) {
    //toast.error(JSON.stringify(error));
    throw error;
  }
  return success;
};

const getNodeChildrenRecursive = async (
  db: SQLiteDBConnection,
  externalId: number,
  parentId: number | null,
  table: "Meetings" | "Boards"
) => {
  let nodesTree: INode[] = [];
  try {
    let sqliteValues: capSQLiteValues;
    if (table === "Meetings")
      sqliteValues = await db.query(
        queries.Nodes.retriveByMeeting(externalId, parentId)
      );
    else
      sqliteValues = await db.query(
        queries.BoardNodes.retriveByBoard(externalId, parentId)
      );

    if (sqliteValues && sqliteValues.values) {
      await Promise.all(
        sqliteValues.values.map(async (n) => {
          let node: INode = {
            key: n.Id,
            title: n.Title,
            children: [],
            hasContent: n.ContentId ? n.ContentId > 0 : false,
            markAsNew: false,
            typology: 0
          };
          nodesTree.push(node);
          node.children = await getNodeChildrenRecursive(
            db,
            externalId,
            n.Id,
            table
          );
        })
      );
    }
  } catch (error) {
    //toast.error(JSON.stringify(error));
    nodesTree = [];
  }
  return nodesTree;
};
